import { useCallback, useMemo } from "react";

import { selectSuggestion } from "../../AutocompleteDropdown/selectSuggestion";
import { useStructuredSuggestions } from "../../AutocompleteDropdown/useStructuredSuggestions";
import type { ValueSuggestionItem } from "../../AutocompleteDropdown/types";
import { getOperatorsForFilter, usesStaticValueSuggestions } from "../../config/schema-utils";
import { isNestedFilterType } from "../../config/filter-value-type-behavior";
import { getFilterDefinitionByKey } from "../../config/structured-search-schema";
import type { StructuredSearchStore } from "../../stores";
import { getInputPlaceholder, isStructuredSearchApplyShortcut } from "../value-utils";

interface UseFilterBuilderActionsParams {
  store: StructuredSearchStore;
  focusInput: () => void;
  placeholder: string;
  onStructuredKeyDown: (event: React.KeyboardEvent) => void;
}

export const useFilterBuilderActions = ({
  store,
  focusInput,
  placeholder,
  onStructuredKeyDown,
}: UseFilterBuilderActionsParams) => {
  const {
    suggestions,
    nestedKeyMode,
    isPromptField,
    isTraceVersionedEntityField,
    promptVersionStep,
    isAwaitingCurrentInputSuggestions,
  } = useStructuredSuggestions();

  const applyCurrentFilter = useCallback(() => {
    store.completeFilter();
  }, [store]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      store.setInputText(value);

      if (store.step === "value") {
        store.setValue(value);
      } else if (store.step === "idle") {
        store.openFieldSuggest();
      } else if (store.step === "field") {
        store.setDropdownOpen(true);
        store.setKeyboardIndex(0);
      }
    },
    [store],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (
        store.step === "value" &&
        store.isMultiSelectMode &&
        isStructuredSearchApplyShortcut(event)
      ) {
        event.preventDefault();
        event.stopPropagation();
        applyCurrentFilter();
        return;
      }

      if (store.step === "value" && event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        const rawInputValue = store.inputText;
        const trimmedInputValue = rawInputValue.trim();
        const highlightedIndex = Math.min(store.keyboardIndex, Math.max(0, suggestions.length - 1));
        // While waiting on typed input, ignore stale highlighted suggestions from the previous list.
        const highlightedSuggestion =
          !isAwaitingCurrentInputSuggestions &&
          suggestions.length > 0 &&
          (store.dropdownOpen || store.isMultiSelectMode)
            ? (suggestions[highlightedIndex] as ValueSuggestionItem | undefined)
            : undefined;

        const usesFixedValueSuggestions = usesStaticValueSuggestions(
          store.schema,
          store.inProgress.field,
        );

        if (usesFixedValueSuggestions && highlightedSuggestion) {
          selectSuggestion(store, highlightedSuggestion, {
            nestedKeyMode,
            isPromptField,
            isTraceVersionedEntityField,
            versionStep: promptVersionStep,
          });
          return;
        }

        const opDef = getOperatorsForFilter(store.schema, store.inProgress.field).find(
          op => op.operator === store.inProgress.operator,
        );
        const canApplyTypedValueDirectly =
          !nestedKeyMode &&
          !isPromptField &&
          !isTraceVersionedEntityField &&
          !promptVersionStep &&
          (opDef?.valueInput === "text" || opDef?.valueInput === "number");
        const shouldApplyTypedValueNow =
          canApplyTypedValueDirectly &&
          trimmedInputValue.length > 0 &&
          (suggestions.length === 0 || isAwaitingCurrentInputSuggestions || !highlightedSuggestion);

        if (shouldApplyTypedValueNow) {
          if (store.isMultiSelectMode) {
            store.addMultiValue(rawInputValue);
          } else {
            store.setValue(rawInputValue);
            store.completeFilter();
          }
          return;
        }

        if (
          store.isMultiSelectMode &&
          (isPromptField || isTraceVersionedEntityField) &&
          trimmedInputValue.length > 0 &&
          (trimmedInputValue.includes(",") ||
            !highlightedSuggestion ||
            isAwaitingCurrentInputSuggestions)
        ) {
          store.setValue(rawInputValue);
          store.completeFilter();
          return;
        }

        if ((isPromptField || isTraceVersionedEntityField) && highlightedSuggestion) {
          selectSuggestion(store, highlightedSuggestion, {
            nestedKeyMode,
            isPromptField,
            isTraceVersionedEntityField,
            versionStep: promptVersionStep,
          });
          return;
        }

        if (store.isMultiSelectMode) {
          if (highlightedSuggestion) {
            selectSuggestion(store, highlightedSuggestion, {
              nestedKeyMode,
              isPromptField,
              isTraceVersionedEntityField,
              versionStep: promptVersionStep,
            });
            return;
          }

          const trimmed = store.inputText.trim();
          if (trimmed) {
            store.addMultiValue(trimmed);
            return;
          }

          if (store.multiValues.length > 0) {
            store.completeFilter();
          }
          return;
        }

        if (highlightedSuggestion) {
          selectSuggestion(store, highlightedSuggestion, {
            nestedKeyMode,
            isPromptField,
            isTraceVersionedEntityField,
            versionStep: promptVersionStep,
          });
          return;
        }

        store.completeFilter();
        return;
      }

      onStructuredKeyDown(event);
    },
    [
      applyCurrentFilter,
      isAwaitingCurrentInputSuggestions,
      isPromptField,
      isTraceVersionedEntityField,
      nestedKeyMode,
      onStructuredKeyDown,
      promptVersionStep,
      store,
      suggestions,
    ],
  );

  const handleFieldPillClick = useCallback(() => {
    if (store.step !== "value" && store.step !== "operator" && store.step !== "key") {
      return;
    }

    const fieldDisplay =
      getFilterDefinitionByKey(store.schema, store.inProgress.field)?.label ??
      store.inProgress.field ??
      "";
    store.setStep("field");
    store.setInputText(fieldDisplay);
    store.setDropdownOpen(true);
    store.setKeyboardIndex(0);
    focusInput();
  }, [focusInput, store]);

  const handleKeyPillClick = useCallback(() => {
    if (store.step !== "value" && store.step !== "operator" && store.step !== "key") {
      return;
    }
    if (
      !store.inProgress.type ||
      !isNestedFilterType(store.inProgress.type) ||
      !store.inProgress.nestedKey
    ) {
      return;
    }
    store.editNestedKey();
    focusInput();
  }, [focusInput, store]);

  const handleOperatorPillClick = useCallback(() => {
    if (store.step !== "value") {
      return;
    }

    const opDef = getOperatorsForFilter(store.schema, store.inProgress.field).find(
      op => op.operator === store.inProgress.operator,
    );
    const opDisplay = opDef?.display ?? store.inProgress.operator ?? "";
    store.setStep("operator");
    store.setInputText(opDisplay);
    store.setDropdownOpen(true);
    store.setKeyboardIndex(0);
    focusInput();
  }, [focusInput, store]);

  const textPlaceholder = useMemo(
    () =>
      getInputPlaceholder({
        isMultiSelect: store.isMultiSelectMode,
        multiCount: store.multiValues.length,
        step: store.step,
        fallback: placeholder,
        operator: store.inProgress.operator,
      }),
    [
      placeholder,
      store.isMultiSelectMode,
      store.multiValues.length,
      store.step,
      store.inProgress.operator,
    ],
  );

  return {
    applyCurrentFilter,
    handleChange,
    handleFieldPillClick,
    handleInputKeyDown,
    handleKeyPillClick,
    handleOperatorPillClick,
    textPlaceholder,
  };
};
